import React, { useState, useEffect } from "react";
import {
  User,
  Lock,
  Camera,
  Save,
  Loader,
  Key,
  Copy,
  Check,
  Eye,
  EyeOff,
  Upload,
  X as CloseIcon,
  AlertCircle,
  CheckCircle,
  Shield,
  Mail,
} from "lucide-react";
import apiService from "../../services/api";
import tokenManager from "../../utils/tokenManager";
import TwoFactorSettings from "./TwoFactorSettings";
import EmailConnection from "./EmailConnection";
import { useTheme } from "../../contexts/ThemeContext";

// Merges a partial API response into the full stored user so that fields
// like `teams` (which the update endpoints don't return) are preserved.
const mergeUserUpdate = (updatedFields) => {
  const existingUser = tokenManager.getUser() || {};
  return { ...existingUser, ...updatedFields };
};

// Derive the initial tab from the current URL
const getInitialTab = () => {
  const path = window.location.pathname;
  if (path.includes("/settings/email")) return "email";
  return "personal";
};

const SettingsPage = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync URL when the email tab is active / deactivated
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "email") {
      window.history.pushState(null, "", "/settings/email");
    } else if (window.location.pathname.includes("/settings/email")) {
      window.history.pushState(null, "", "/settings");
    }
  };

  // Handle browser back/forward navigating into or away from /settings/email
  useEffect(() => {
    const onPopState = () => {
      setActiveTab(getInitialTab());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [copied, setCopied] = useState(false);

  // Personal Details State
  const [personalDetails, setPersonalDetails] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Profile Picture State
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchToken();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMe();

      if (response.success) {
        setUser(response.user);
        setPersonalDetails({
          name: response.user.name,
          email: response.user.email,
          phoneNumber: response.user.phoneNumber,
        });
        setImagePreview(response.user.profileImageUrl);
        tokenManager.setUser(response.user);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      showMessage(
        error.response?.data?.message || "Failed to load user data",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchToken = () => {
    const authToken = tokenManager.getAccessToken();
    setToken(authToken);
  };

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  const handlePersonalDetailsChange = (e) => {
    setPersonalDetails({ ...personalDetails, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showMessage("Image size must be less than 5MB", "error");
        return;
      }
      if (!file.type.startsWith("image/")) {
        showMessage("Please select a valid image file", "error");
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(user?.profileImageUrl || null);
  };

  const savePersonalDetails = async () => {
    try {
      setSaving(true);
      const response = await apiService.updatePersonalDetails(personalDetails);
      if (response.success) {
        const fullUser = mergeUserUpdate(response.user);
        setUser(fullUser);
        tokenManager.setUser(fullUser);
        window.dispatchEvent(new Event("userChanged"));
        showMessage("Personal details updated successfully", "success");
      }
    } catch (error) {
      console.error("Error updating personal details:", error);
      showMessage(
        error.response?.data?.message || "Failed to update personal details",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      showMessage("Please fill in all password fields", "error");
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage("New passwords do not match", "error");
      return;
    }
    if (passwordData.newPassword.length < 8) {
      showMessage("Password must be at least 8 characters long", "error");
      return;
    }
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_#\-+=^(){}[\]|\\:;"'<>,.~`])[A-Za-z\d@$!%*?&_#\-+=^(){}[\]|\\:;"'<>,.~`]+$/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      showMessage(
        "Password must contain uppercase, lowercase, number and special character",
        "error",
      );
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      if (response.success) {
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        showMessage("Password changed successfully", "success");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showMessage(
        error.response?.data?.message || "Failed to change password",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const uploadProfilePicture = async () => {
    if (!profileImage) {
      showMessage("Please select an image first", "error");
      return;
    }
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("profileImage", profileImage);
      const response = await apiService.uploadProfileImage(formData);
      if (response.success) {
        const fullUser = mergeUserUpdate(response.user);
        setUser(fullUser);
        setProfileImage(null);
        setImagePreview(response.profileImageUrl);
        tokenManager.setUser(fullUser);
        window.dispatchEvent(new Event("userChanged"));
        showMessage("Profile picture updated successfully", "success");
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      showMessage(
        error.response?.data?.message || "Failed to upload profile picture",
        "error",
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const copyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showMessage("Token copied to clipboard", "success");
    }
  };

  const regenerateToken = async () => {
    if (
      !window.confirm(
        "Are you sure you want to regenerate your API token? This will invalidate your current token.",
      )
    ) {
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.regenerateToken();
      if (response.success) {
        setToken(response.token);
        tokenManager.setTokens(response.token);
        showMessage("Token regenerated successfully", "success");
      }
    } catch (error) {
      console.error("Error regenerating token:", error);
      showMessage(
        error.response?.data?.message || "Failed to regenerate token",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
        }`}
      >
        <Loader
          className={`animate-spin ${isDarkMode ? "text-white" : "text-gray-700"}`}
          size={32}
        />
      </div>
    );
  }

  // Reusable class helpers
  const cardBg = isDarkMode
    ? "bg-[#13161B] border-gray-700"
    : "bg-white border-gray-200";
  const inputCls = isDarkMode
    ? "bg-gray-800 border-gray-700 text-white focus:border-blue-500"
    : "bg-gray-50 border-gray-300 text-gray-900 focus:border-blue-500";
  const labelCls = isDarkMode ? "text-gray-400" : "text-gray-500";
  const readonlyCls = isDarkMode
    ? "bg-gray-800/50 border-gray-700 text-gray-400"
    : "bg-gray-100 border-gray-200 text-gray-500";
  const dividerCls = isDarkMode ? "border-gray-700" : "border-gray-200";
  const navInactiveCls = isDarkMode
    ? "text-gray-400 hover:bg-gray-800 hover:text-white"
    : "text-gray-500 hover:bg-gray-100 hover:text-gray-900";

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* Header */}
      <div
        className={`border-b ${
          isDarkMode
            ? "border-gray-700 bg-[#13161B]"
            : "border-gray-200 bg-white"
        }`}
      >
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Settings</h1>
              <p className={labelCls}>
                Manage your account settings and preferences
              </p>
            </div>
            {user && (
              <div
                className={`flex items-center gap-3 rounded-lg p-2 sm:p-3 ${
                  isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                }`}
              >
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                    isDarkMode ? "bg-gray-700" : "bg-gray-300"
                  }`}
                >
                  {user.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span
                      className={`text-base sm:text-xl font-medium ${
                        isDarkMode ? "text-white" : "text-gray-700"
                      }`}
                    >
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="hidden sm:block">
                  <div className="font-medium">{user.name}</div>
                  <div className={`text-sm ${labelCls}`}>{user.email}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Message Bar */}
      {message && (
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div
            className={`flex items-center gap-3 p-4 rounded-lg ${
              message.type === "success"
                ? "bg-green-500/10 border border-green-500/30 text-green-400"
                : "bg-red-500/10 border border-red-500/30 text-red-400"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            {/* Mobile: horizontal scrolling tab bar */}
            <div
              className={`lg:hidden flex overflow-x-auto scrollbar-hide gap-1 rounded-lg border p-1.5 ${cardBg}`}
            >
              {[
                { id: "personal", icon: User, label: "Personal" },
                { id: "password", icon: Lock, label: "Password" },
                { id: "profile-picture", icon: Camera, label: "Picture" },
                { id: "two-factor", icon: Shield, label: "2FA" },
                { id: "api-token", icon: Key, label: "API Token" },
                { id: "email", icon: Mail, label: "Email" },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-sm whitespace-nowrap ${
                    activeTab === id ? "bg-blue-600 text-white" : navInactiveCls
                  }`}
                >
                  <Icon size={15} />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
            {/* Desktop: vertical sidebar */}
            <div className={`hidden lg:block rounded-lg border p-2 ${cardBg}`}>
              {[
                { id: "personal", icon: User, label: "Personal Details" },
                { id: "password", icon: Lock, label: "Password" },
                {
                  id: "profile-picture",
                  icon: Camera,
                  label: "Profile Picture",
                },
                { id: "two-factor", icon: Shield, label: "Two-Factor Auth" },
                { id: "api-token", icon: Key, label: "API Token" },
                { id: "email", icon: Mail, label: "Email Connection" },
              ].map(({ id, icon: Icon, label }, idx) => (
                <button
                  key={id}
                  onClick={() => handleTabChange(id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    idx > 0 ? "mt-1" : ""
                  } ${
                    activeTab === id ? "bg-blue-600 text-white" : navInactiveCls
                  }`}
                >
                  <Icon size={18} />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Personal Details Tab */}
            {activeTab === "personal" && (
              <div className={`rounded-lg border p-4 sm:p-6 ${cardBg}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Personal Details</h2>
                    <p className={`text-sm ${labelCls}`}>
                      Update your personal information
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${labelCls}`}
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={personalDetails.name}
                      onChange={handlePersonalDetailsChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none ${inputCls}`}
                      placeholder="Enter your full name"
                      readOnly
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${labelCls}`}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={personalDetails.email}
                      onChange={handlePersonalDetailsChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none ${inputCls}`}
                      placeholder="Enter your email"
                      readOnly
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${labelCls}`}
                    >
                      Phone Number
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={personalDetails.phoneNumber}
                      onChange={handlePersonalDetailsChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none ${inputCls}`}
                      placeholder="Enter your phone number"
                      readOnly
                    />
                  </div>

                  {user?.company && (
                    <div>
                      <label
                        className={`block text-sm font-medium mb-2 ${labelCls}`}
                      >
                        Company
                      </label>
                      <div
                        className={`px-4 py-3 border rounded-lg ${readonlyCls}`}
                      >
                        {user.company.name}
                      </div>
                    </div>
                  )}

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${labelCls}`}
                    >
                      Role
                    </label>
                    <div
                      className={`px-4 py-3 border rounded-lg capitalize ${readonlyCls}`}
                    >
                      {user?.role?.replace("_", " ")}
                    </div>
                  </div>
                </div>

                <div
                  className={`flex flex-col sm:flex-row sm:justify-end mt-4 sm:mt-6 pt-4 sm:pt-6 border-t ${dividerCls}`}
                >
                  <button
                    onClick={savePersonalDetails}
                    disabled
                    className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader size={18} className="animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === "password" && (
              <div className={`rounded-lg border p-4 sm:p-6 ${cardBg}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Lock size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Change Password</h2>
                    <p className={`text-sm ${labelCls}`}>
                      Update your password to keep your account secure
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    {
                      field: "current",
                      name: "currentPassword",
                      label: "Current Password",
                      placeholder: "Enter current password",
                    },
                    {
                      field: "new",
                      name: "newPassword",
                      label: "New Password",
                      placeholder: "Enter new password",
                      hint: "Password must be at least 8 characters long",
                    },
                    {
                      field: "confirm",
                      name: "confirmPassword",
                      label: "Confirm New Password",
                      placeholder: "Confirm new password",
                    },
                  ].map(({ field, name, label, placeholder, hint }) => (
                    <div key={field}>
                      <label
                        className={`block text-sm font-medium mb-2 ${labelCls}`}
                      >
                        {label}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords[field] ? "text" : "password"}
                          name={name}
                          value={passwordData[name]}
                          onChange={handlePasswordChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none pr-12 ${inputCls}`}
                          placeholder={placeholder}
                          readOnly
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility(field)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                            isDarkMode
                              ? "text-gray-400 hover:text-white"
                              : "text-gray-400 hover:text-gray-700"
                          }`}
                        >
                          {showPasswords[field] ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                      {hint && (
                        <p className={`text-xs mt-1 ${labelCls}`}>{hint}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div
                  className={`flex flex-col sm:flex-row sm:justify-end mt-4 sm:mt-6 pt-4 sm:pt-6 border-t ${dividerCls}`}
                >
                  <button
                    onClick={savePassword}
                    disabled
                    className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader size={18} className="animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        <span>Update Password</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Profile Picture Tab */}
            {activeTab === "profile-picture" && (
              <div className={`rounded-lg border p-4 sm:p-6 ${cardBg}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Camera size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">Profile Picture</h2>
                    <p className={`text-sm ${labelCls}`}>
                      Upload a profile picture to personalize your account
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center">
                  <div
                    className={`w-40 h-40 rounded-full border-4 overflow-hidden mb-6 relative group ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-gray-100 border-gray-200"
                    }`}
                  >
                    {imagePreview ? (
                      <>
                        <img
                          src={imagePreview}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                        {profileImage && (
                          <button
                            onClick={handleRemoveImage}
                            className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <CloseIcon size={24} className="text-white" />
                          </button>
                        )}
                      </>
                    ) : (
                      <div
                        className={`w-full h-full flex items-center justify-center text-6xl font-bold ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      >
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="w-full max-w-md">
                    <label
                      className={`flex flex-col items-center px-6 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                        isDarkMode
                          ? "bg-gray-800/50 border-gray-700 hover:bg-gray-800"
                          : "bg-gray-50 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      <Upload size={32} className={`mb-3 ${labelCls}`} />
                      <span className={`text-sm text-center mb-2 ${labelCls}`}>
                        Click to upload or drag and drop
                      </span>
                      <span
                        className={`text-xs ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        PNG, JPG or JPEG (max 5MB)
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageSelect}
                        readOnly
                      />
                    </label>

                    {profileImage && (
                      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle size={18} className="text-blue-400" />
                            <div>
                              <div className="text-sm font-medium text-blue-400">
                                {profileImage.name}
                              </div>
                              <div className={`text-xs ${labelCls}`}>
                                {(profileImage.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={handleRemoveImage}
                            className={`transition-colors ${
                              isDarkMode
                                ? "text-gray-400 hover:text-white"
                                : "text-gray-400 hover:text-gray-700"
                            }`}
                          >
                            <CloseIcon size={18} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {profileImage && (
                  <div
                    className={`flex flex-col sm:flex-row sm:justify-end mt-4 sm:mt-6 pt-4 sm:pt-6 border-t ${dividerCls}`}
                  >
                    <button
                      onClick={uploadProfilePicture}
                      disabled
                      className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploadingImage ? (
                        <>
                          <Loader size={18} className="animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          <span>Upload Picture</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "two-factor" && (
              <TwoFactorSettings user={user} showMessage={showMessage} />
            )}

            {/* Email Connection Tab */}
            {activeTab === "email" && (
              <div
                className={`rounded-lg border p-0 overflow-hidden ${cardBg}`}
              >
                <EmailConnection />
              </div>
            )}

            {/* API Token Tab */}
            {activeTab === "api-token" && (
              <div className={`rounded-lg border p-4 sm:p-6 ${cardBg}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Key size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">API Token</h2>
                    <p className={`text-sm ${labelCls}`}>
                      Use this token to authenticate API requests
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={20} className="text-yellow-400 mt-0.5" />
                    <div className="text-sm text-yellow-400">
                      <strong className="font-medium">
                        Keep your token secure!
                      </strong>
                      <p className="mt-1 text-yellow-400/80">
                        Never share your API token publicly or commit it to
                        version control. If you believe your token has been
                        compromised, regenerate it immediately.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${labelCls}`}
                  >
                    Your API Token
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={token || "No token available"}
                      readOnly
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none font-mono text-sm ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                    <button
                      onClick={copyToken}
                      className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 sm:py-3 rounded-lg transition-colors ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                      }`}
                      disabled
                    >
                      {copied ? (
                        <>
                          <Check size={16} className="text-green-400" />
                          <span className="text-green-400 text-sm">
                            Copied!
                          </span>
                        </>
                      ) : (
                        <>
                          <Copy size={16} />
                          <span className="text-sm">Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className={`mt-6 pt-6 border-t ${dividerCls}`}>
                  <h3 className="font-medium mb-3">Token Management</h3>
                  <p className={`text-sm mb-4 ${labelCls}`}>
                    If you suspect your token has been compromised, you can
                    regenerate it. This will invalidate the old token and create
                    a new one.
                  </p>
                  <button
                    onClick={regenerateToken}
                    disabled
                    className="w-full sm:w-auto justify-center flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader size={18} className="animate-spin" />
                        <span>Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <Key size={18} />
                        <span>Regenerate Token</span>
                      </>
                    )}
                  </button>
                </div>

                <div className={`mt-6 pt-6 border-t ${dividerCls}`}>
                  <h3 className="font-medium mb-3">Example Usage</h3>
                  <div
                    className={`rounded-lg p-4 font-mono text-sm overflow-x-auto ${
                      isDarkMode ? "bg-gray-900" : "bg-gray-100"
                    }`}
                  >
                    <div
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      // JavaScript Example
                    </div>
                    <div className="text-blue-400 mt-2">fetch</div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-800"}
                    >
                      (
                    </div>
                    <div className="text-green-400 ml-4">
                      'https://api.example.com/endpoint'
                    </div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-800"}
                    >
                      , {"{"}
                    </div>
                    <div className="ml-4">
                      <span className="text-purple-400">headers</span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-800"}
                      >
                        : {"{"}
                      </span>
                    </div>
                    <div className="ml-8">
                      <span className="text-green-400">'Authorization'</span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-800"}
                      >
                        :{" "}
                      </span>
                      <span className="text-green-400">
                        {"`"}Bearer ${"{"}token{"}"}
                        {"`"}
                      </span>
                    </div>
                    <div
                      className={`ml-4 ${isDarkMode ? "text-white" : "text-gray-800"}`}
                    >
                      {"}"}
                    </div>
                    <div
                      className={isDarkMode ? "text-white" : "text-gray-800"}
                    >
                      {"})"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
